import React, { useEffect, useState } from "react";
import Spacing from "../../Spacing";
import { FileCog, Hospital, Search } from "lucide-react";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { can_visit, getPractice, notify, numberWithCommas } from "../../../helpers";
import NPIManagementDataTable from "../../DataTable/NPIManagementDataTable";
import { addNewNPI, deleteNPI, fetchAllNPIs, updateNPI } from "../../../services/npiManagement";
import AddAndEditNPIModal from "../../Modal/AddAndEditNPIModal";

export default function NPIManagement({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }
  const [npiList, setNpiList] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [addNPIModal, setAddNPIModal] = useState(false);
  const [editNPIModal, setEditNPIModal] = useState(false);
  const [npiActionLoader, setNpiActionLoader] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [addInsuranceModal, setAddInsuranceModal] = useState(false);

  const [filter, setFilter] = useState({
    location_id: location_id,
    account_id: account_id,
    search_term: "",
    order_by: [],
  });
  const [paginationCount, setPaginationCount] = useState({ totalCount: 0 });

  const loadAllNPIs = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchAllNPIs( filter, account_id, location_id );
      setNpiList(response.data?.results || []);
      setPaginationCount({
        totalCount: response.data.total,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    loadAllNPIs(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleChangeSearchTerm = (event) => {
    setFilter({ ...filter, search_term: event.target.value });
    setDataLoading(true);
  };

  const handleAddNPIInfo = async (data) => {
    if(can_visit("practice_npi_management")){
      setNpiActionLoader(true);
      try {
        const response = await addNewNPI({ ...data });
        if(response){
          if(response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.status === "success"){
              notify("success", response?.data?.message, 5);
              await loadAllNPIs(filter);
            } else {
              notify("error", response?.data?.message, 5);
            }
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail);
        }
      } finally {
        setNpiActionLoader(false);
        handleDrawerClose(true);
      }
    } else {
      handleDrawerClose(true);
    }
  };

  const handleUpdatedNPIInfo = async (data) => {
    if(can_visit("practice_npi_management")){
      setNpiActionLoader(true);
      try {
        const response = await updateNPI({ ...data });
        if(response){
          if(response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.status === "success"){
              notify("success", response?.data?.message, 5);
              await loadAllNPIs(filter);
            } else {
              notify("error", response?.data?.message, 5);
            }
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail);
        }
      } finally {
        setNpiActionLoader(false);
        handleDrawerClose(false);
      }
    } else {
      handleDrawerClose(false);
    }
  };

  const handleDrawerClose = (add=true) => {
    if(add) {
      setAddNPIModal(false);
    } else {
      setEditNPIModal(false);
    }
    window.scrollTo(0, previousScrollPosition);
  };

  const handleDeleteRecord = async (id) => {
    try {
      if(can_visit("practice_npi_management")){
        var response = await deleteNPI(id, account_id, location_id);
        if (response?.detail) {
          notify("error", response?.detail);
        } else if(response?.data?.status === 'success'){
          await loadAllNPIs(filter);
          notify("success", response?.data?.message);
        } else {
          notify("error",  response?.data?.message);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail);
      }
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useDocumentTitle("NPI Management");

  return (
    <>
      <div className="docpro_page_heading_wrap mb-0">
        <h2 className="docpro_page_heading docpro_fs_24">
          NPI Management
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {(can_visit("settings") && can_visit("practice_npi_management")) && (
            <>
              <button
                className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
                onClick={() => {
                  setAddNPIModal(true);
                  setPreviousScrollPosition(window.scrollY);
                  window.scrollTo(0, 0);
                }}
              >
                <Hospital width={20} height={20} />
                <span>Add NPI</span>
              </button>
              <button
                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                onClick={() => {
                  setAddInsuranceModal(true);
                  setPreviousScrollPosition(window.scrollY);
                  window.scrollTo(0, 0);
                }}
              >
                <FileCog width={20} height={20} />
                <span>Manage Insurances</span>
              </button>
            </>
          )} 
        </div>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form">
            <input
              type="text"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>
        </div>

        <NPIManagementDataTable
          account_id={account_id}
          location_id={location_id}
          tableData={npiList || []}
          handleDeleteRecord={handleDeleteRecord}
          reloadData={loadAllNPIs}
          reloadDataFilter={filter}
          handleUpdatedNPIInfo={handleUpdatedNPIInfo}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          npiActionLoader={npiActionLoader}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
          editNPIModal={editNPIModal}
          setEditNPIModal={setEditNPIModal}
        />
      </div>

      {addNPIModal && (
        <AddAndEditNPIModal
          editModal={false}
          openModal={addNPIModal}
          setOpenModal={setAddNPIModal}
          account_id={account_id}
          location_id={location_id}
          handleNPIAction={handleAddNPIInfo}
          npiActionLoader={npiActionLoader}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {addInsuranceModal && (
        <AddAndEditNPIModal
          editModal={false}
          openModal={addInsuranceModal}
          setOpenModal={setAddInsuranceModal}
          account_id={account_id}
          location_id={location_id}
          handleNPIAction={handleUpdatedNPIInfo}
          npiActionLoader={npiActionLoader}
          previousScrollPosition={previousScrollPosition}
          manageInsurance={true}
        />
      )}
    </>
  );
}
